'use client';

import useEmblaCarousel from 'embla-carousel-react';
import Image from 'next/image';
import { useState, useEffect, useCallback } from 'react';

import ChevronIcon from '@/fo/icons/base/icons/arrow_thin.svg';
import { UiKitButtonBase } from '@/fo/ui/kit/button/base';
import { UiKitLabelBase } from '@/fo/ui/kit/label/base';
import { UiKitLightboxBase } from '@/fo/ui/kit/lightbox/base';
import { useMediaQuery } from '@/fo/ui/mod/media/query';
import { utilEventConfigurableProduct } from '@/fo/util/event-configurable-product';
import { MappedProduct } from '@/fo/util/map-product-data';
const cursorUrl = '/media/icons/plus-circle.svg';

import {
  PrevButton,
  NextButton,
  usePrevNextButtons
} from '../components/Arrows/Arrows';
import { Thumb } from '../components/Thumb/Thumb';

import { styles } from './UiProductPictureMain.styles';

export interface UiProductPictureMainProps {
  className?: string;
  selectedProduct: MappedProduct;
}

export function UiProductPictureMain({
  className,
  selectedProduct
}: UiProductPictureMainProps) {
  const slots = styles();
  const isDesktop = useMediaQuery('sm');
  const [product, setProduct] = useState(selectedProduct);

  const { mediaGallery: images, labels: tags } = product;

  const [isOpen, setIsOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [prevArrowVisible, setPrevArrowVisible] = useState(true);
  const [nextArrowVisible, setNextArrowVisible] = useState(true);
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true });
  const [emblaThumbsRef, emblaThumbsApi] = useEmblaCarousel({
    axis: isDesktop ? 'y' : 'x'
  });
  const { onPrevButtonClick, onNextButtonClick } = usePrevNextButtons(emblaApi);

  const onThumbClick = (index: number) => {
    if (!emblaApi) return;

    emblaApi.scrollTo(index);
  };

  const onSelect = useCallback(() => {
    if (!emblaApi || !emblaThumbsApi) return;
    setSelectedIndex(emblaApi.selectedScrollSnap());

    emblaThumbsApi.scrollTo(emblaApi.selectedScrollSnap());

    setPrevArrowVisible(true);
    setNextArrowVisible(true);
  }, [emblaApi, emblaThumbsApi]);

  useEffect(() => {
    return utilEventConfigurableProduct.onProductSelect((product) => {
      setProduct(product);
    });
  }, []);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();

    emblaApi.on('select', onSelect).on('reInit', onSelect);

    return () => {
      emblaApi.off('select', onSelect).off('reInit', onSelect);
    };
  }, [emblaApi, onSelect]);

  if (!images.length) {
    images.push({
      id: 'placeholder',
      type: 'image',
      src: '/public/placeholder.png',
      alt: 'placeholder',
      roles: []
    });
  }

  return (
    <div className={slots.base({ className })}>
      {(tags || selectedProduct.discount) && (
        <div className={slots.tags()}>
          {selectedProduct.discount && (
            <UiKitLabelBase>{selectedProduct.discount}</UiKitLabelBase>
          )}
          {tags?.map(({ name, color }) => (
            <UiKitLabelBase
              key={name}
              customColor={color}
            >
              {name}
            </UiKitLabelBase>
          ))}
          {/* {productOverlays?.map((overlay) => (
            <div
              key={overlay.id}
              className={slots.productOverlay()}
              dangerouslySetInnerHTML={{ __html: overlay.prodText }}
            />
          ))} */}
        </div>
      )}
      {images.length > 0 && (
        <div className={slots.thumbs({ hide: images.length === 1 })}>
          <UiKitButtonBase
            onClick={() => emblaApi?.scrollPrev()}
            className={slots.thumbArrow({
              className: 'rotate-90',
              hide: !prevArrowVisible
            })}
            rounded="full"
            color="white"
          >
            <ChevronIcon className={slots.thumbArrowIcon()} />
          </UiKitButtonBase>

          <div
            ref={emblaThumbsRef}
            className={slots.thumbsViewport()}
          >
            <div className={slots.thumbsContainer()}>
              {images.map((image, index) => (
                <Thumb
                  key={index}
                  onClick={() => onThumbClick(index)}
                  selected={index === selectedIndex}
                >
                  {image.type === 'video' && (
                    <video
                      className="h-20 w-20 object-cover"
                      width={80}
                      height={80}
                    >
                      <source
                        src={image.src}
                        type="video/mp4"
                      />
                    </video>
                  )}
                  {image.type === 'image' && (
                    <Image
                      className={slots.image()}
                      src={image.src}
                      alt=""
                      width={80}
                      height={80}
                    />
                  )}
                </Thumb>
              ))}
            </div>
          </div>

          <UiKitButtonBase
            onClick={() => emblaApi?.scrollNext()}
            className={slots.thumbArrow({
              className: '-rotate-90',
              hide: !nextArrowVisible
            })}
            rounded="full"
            color="white"
          >
            <ChevronIcon className={slots.thumbArrowIcon()} />
          </UiKitButtonBase>
        </div>
      )}
      <div
        className={slots.viewport()}
        ref={emblaRef}
      >
        <div className={slots.container()}>
          {images.map((image) => (
            <div
              className={slots.imageWrapper()}
              key={image.src}
              onClick={() => setIsOpen(!isOpen)}
              style={{
                cursor: `url(${cursorUrl}), pointer`
              }}
            >
              {image.type === 'video' && (
                <video
                  className="h-150 w-155 object-cover"
                  autoPlay
                  muted
                  width={600}
                  height={600}
                >
                  <source
                    src={image.src}
                    type="video/mp4"
                  />
                </video>
              )}
              {image.type === 'image' && (
                <Image
                  className={slots.image()}
                  src={image.src}
                  alt=""
                  width={600}
                  height={600}
                />
              )}
            </div>
          ))}
        </div>

        {images.length > 1 && (
          <>
            <PrevButton onClick={onPrevButtonClick} />
            <NextButton onClick={onNextButtonClick} />
          </>
        )}
      </div>
      {isOpen && (
        <UiKitLightboxBase
          images={images}
          open={isOpen}
          onOpenChange={() => setIsOpen(!isOpen)}
          initialIndexSlide={selectedIndex}
        />
      )}
    </div>
  );
}
